// IMPORTANT: Updating imports here should be added to the `manualChunk.initialTheme`
// entry in `vite.config.ts`
import React, { useLayoutEffect } from 'react';
import { MaestroProvider, useEnhancedEffect } from '@landr/maestro';
import { getTheme, injectTheme } from '@landr/styled-system/themes';
import { useFlag } from '@landr/mfe';
import {
    FeatureFlags,
    featureFlagService,
} from '@landr/root-app/feature-flags';
import { log } from 'utils/log';
import { EventIds } from './constants';

const retrieveTheme = async (hasNewTheme: boolean) => {
    const legacyLANDRTheme = await getTheme('landr');
    const newLANDRTheme = await getTheme('landr2025');

    return hasNewTheme ? newLANDRTheme : legacyLANDRTheme;
};

export const AppStyleProvider = ({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement => {
    useEnhancedEffect(() => {
        document.documentElement.classList.remove('light');
        document.documentElement.classList.add('dark');
    }, []);

    const hasGlobalThemeToggle = useFlag(FeatureFlags.GlobalThemeToggle);
    const hasNewTheme = useFlag(FeatureFlags.NewLANDRTheme);

    const appConfig = featureFlagService.getAppConfig();
    const isLandr = appConfig?.name.toLocaleLowerCase() === 'landr';

    useLayoutEffect(() => {
        const loadAndInjectTheme = async () => {
            try {
                const theme = await retrieveTheme(hasNewTheme);

                isLandr && injectTheme(document.documentElement, theme);
            } catch (error) {
                log.error(
                    `Error while loading/injecting theme`,
                    EventIds.LoadInjectThemeError,
                    error,
                );
            }
        };

        loadAndInjectTheme();
    }, [hasNewTheme, isLandr]);

    useEnhancedEffect(() => {
        if (hasGlobalThemeToggle) {
            document.documentElement.classList.remove('light', 'dark');
        } else {
            document.documentElement.classList.remove('light');
            document.documentElement.classList.add('dark');

            document.documentElement.removeAttribute('data-color-mode');
        }
    }, [hasGlobalThemeToggle]);

    return (
        <MaestroProvider
            inheritColorMode={hasGlobalThemeToggle}
            forceColorMode={hasGlobalThemeToggle ? undefined : 'dark'}
        >
            {children}
        </MaestroProvider>
    );
};

export default AppStyleProvider;
